if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initMorphingButton();
      }

      static cartState = null;
      static cartRevision = 0;
      static bagCountRevision = -1;
      static cartRequest = null;
      static mutationQueue = Promise.resolve();

      static cacheCart(cart) {
        if (this.cartState !== cart) {
          this.cartState = cart;
          this.cartRevision += 1;
        }
        return cart;
      }

      static readCart(force = false) {
        if (!force && this.cartState) return Promise.resolve(this.cartState);
        if (!this.cartRequest) {
          const revision = this.cartRevision;
          this.cartRequest = fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' })
            .then((response) => {
              if (!response.ok) throw new Error('Your bag could not be loaded. Please try again.');
              return response.json();
            })
            .then((cart) => revision === this.cartRevision ? this.cacheCart(cart) : this.cartState)
            .finally(() => { this.cartRequest = null; });
        }
        return this.cartRequest;
      }

      static updateBagCount(cart) {
        const link = document.getElementById('cart-icon-bubble');
        if (!link) return;
        // Preserve the MEROO icon wrapper and visible Bag label.
        let badge = link.querySelector('.meroo-header__cart-count, .cart-count-bubble');
        if (!badge && cart.item_count) {
          badge = document.createElement('span');
          badge.className = 'meroo-header__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          (link.querySelector('div') || link).append(badge);
        }
        if (badge) {
          badge.textContent = cart.item_count < 100 ? cart.item_count : '99+';
          badge.hidden = !cart.item_count;
        }
        link.setAttribute('aria-label', `Bag, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
      }

      initMorphingButton() {
        this.morphWrapper = this.querySelector('.meroo-morph-button-wrapper');
        if (!this.morphWrapper) return;
        this.qtyVal = this.morphWrapper.querySelector('.meroo-inline-qty__val');
        this.inlineQuantity = this.morphWrapper.querySelector('.meroo-inline-qty');
        this.submitButton.removeAttribute('aria-haspopup');
        this.form.addEventListener('change', (event) => {
          if (event.target.name === 'id') this.syncButtonWithCart();
        });
        this.morphWrapper.querySelector('.plus').addEventListener('click', () => this.changeInlineQuantity(1));
        this.morphWrapper.querySelector('.minus').addEventListener('click', () => this.changeInlineQuantity(-1));
      }

      connectedCallback() {
        if (!this.morphWrapper) return;
        this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (Number.isInteger(event.cartData?.item_count)) {
            this.constructor.cacheCart(event.cartData);
            // One bag-count update per cart revision (dozens of cards share this event).
            if (this.constructor.bagCountRevision !== this.constructor.cartRevision) {
              this.constructor.bagCountRevision = this.constructor.cartRevision;
              this.constructor.updateBagCount(event.cartData);
            }
            const variantId = String(this.variantIdInput?.value || '');
            const eventVariant = event.productVariantId != null ? String(event.productVariantId) : '';
            const showingAdded = this.morphWrapper.classList.contains('is-added');
            const inCart = event.cartData.items?.some((item) => String(item.variant_id) === variantId);
            // Skip untouched cards so Add to cart stays snappy on large grids.
            if (showingAdded || inCart || (eventVariant && eventVariant === variantId)) {
              this.syncButtonWithCartData(event.cartData);
            }
          } else this.syncButtonWithCart(true);
        });
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncButtonWithCart());
        this.onPageShow = () => this.syncButtonWithCart(true);
        window.addEventListener('pageshow', this.onPageShow);
        this.syncButtonWithCart();
      }

      disconnectedCallback() {
        this.cartUnsubscribe?.();
        this.variantUnsubscribe?.();
        window.removeEventListener('pageshow', this.onPageShow);
      }

      findCartLine(cart, data = new FormData(this.form)) {
        const plan = data.get('selling_plan') || '';
        const properties = Object.fromEntries([...data.entries()]
          .filter(([key, value]) => key.startsWith('properties[') && String(value))
          .map(([key, value]) => [key.slice(11, -1), String(value)]));
        return cart.items.find((item) => String(item.variant_id) === String(data.get('id')) &&
          String(item.selling_plan_allocation?.selling_plan?.id || '') === String(plan) &&
          JSON.stringify(Object.entries(item.properties || {}).sort()) === JSON.stringify(Object.entries(properties).sort()));
      }

      quantityRules() {
        const option = this.variantIdInput.selectedOptions?.[0];
        const input = this.form.elements.quantity;
        return {
          min: Number(option ? option.dataset.min : input?.dataset.min || input?.min) || 1,
          step: Number(option ? option.dataset.step : input?.dataset.step || input?.step) || 1,
          max: Number(option ? option.dataset.max : input?.dataset.max || input?.max) || Infinity,
        };
      }

      syncButtonWithCart(force = false) {
        return this.constructor.readCart(force).then((cart) => {
          if (this.isConnected) this.syncButtonWithCartData(cart);
          this.constructor.updateBagCount(cart);
          return cart;
        }).catch(() => {});
      }

      syncButtonWithCartData(cart) {
        const quantity = this.findCartLine(cart)?.quantity || 0;
        const hadFocus = this.morphWrapper.contains(document.activeElement);
        const wasAdded = this.morphWrapper.classList.contains('is-added');
        this.morphWrapper.classList.toggle('is-added', quantity > 0);
        this.inlineQuantity.classList.toggle('hidden', !quantity);
        this.inlineQuantity.hidden = !quantity;
        this.qtyVal.textContent = quantity;
        this.morphWrapper.querySelector('.plus').disabled = this.inlineBusy || quantity >= this.quantityRules().max;
        if (!this.inlineBusy && hadFocus && wasAdded !== (quantity > 0)) {
          (quantity ? this.morphWrapper.querySelector('.plus') : this.submitButton).focus({ preventScroll: true });
        }
      }

      setInlineBusy(busy) {
        this.inlineBusy = busy;
        this.morphWrapper.setAttribute('aria-busy', String(busy));
        this.submitButton.setAttribute('aria-disabled', String(busy));
        this.submitButton.classList.toggle('loading', busy);
        this.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
        this.morphWrapper.querySelectorAll('.meroo-inline-qty__btn').forEach((button) => { button.disabled = busy; });
      }

      async runInlineMutation(operation) {
        if (this.inlineBusy) return;
        const previousFocus = document.activeElement;
        const restoreFocus = this.morphWrapper.contains(previousFocus);
        this.handleErrorMessage();
        this.setInlineBusy(true);
        // Include recovery reads in the queue so they cannot overwrite a later mutation.
        const task = this.constructor.mutationQueue.then(async () => {
          try {
            const cart = await operation();
            this.constructor.cacheCart(cart);
            await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'meroo-inline-cart', cartData: cart });
          } catch (error) {
            this.handleErrorMessage(error.message || 'Your bag could not be updated. Please try again.');
            this.dispatchCartErrorEvent(error.message, 'SERVICE_UNAVAILABLE');
            // Shopify can cap quantities even when it returns an inventory error.
            const cart = await this.constructor.readCart(true).catch(() => null);
            if (cart) await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'meroo-inline-cart', cartData: cart });
          } finally {
            this.setInlineBusy(false);
            if (this.constructor.cartState) this.syncButtonWithCartData(this.constructor.cartState);
            if (restoreFocus && (document.activeElement === document.body || this.morphWrapper.contains(document.activeElement))) {
              const added = this.morphWrapper.classList.contains('is-added');
              const target = added ? (previousFocus === this.submitButton ? this.morphWrapper.querySelector('.minus') : previousFocus) : this.submitButton;
              target.focus({ preventScroll: true });
            }
          }
        });
        this.constructor.mutationQueue = task.catch(() => {});
        return task;
      }

      async requestInlineCart(url, config) {
        const response = await fetch(url, config);
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Your bag could not be updated. Please try again.');
        return data;
      }

      addInlineQuantity() {
        if (this.submitButton.disabled || this.inlineBusy) return;
        const formData = new FormData(this.form);
        formData.delete('return_to');
        if (!formData.has('quantity')) formData.set('quantity', this.quantityRules().min);
        const addQty = Number(formData.get('quantity')) || this.quantityRules().min;
        const deferred = this.createCartLinesUpdateEvent(formData.get('id'), addQty);
        // Optimistic UI so the button feels instant while the network request finishes.
        const previousQty = Number(this.qtyVal?.textContent) || 0;
        this.morphWrapper.classList.add('is-added');
        this.inlineQuantity.classList.remove('hidden');
        this.inlineQuantity.hidden = false;
        this.qtyVal.textContent = previousQty + addQty;
        return this.runInlineMutation(async () => {
          try {
            // Finish an initial read before the mutation so it cannot overwrite newer data.
            if (this.constructor.cartRequest) await this.constructor.cartRequest;
            await this.requestInlineCart(window.routes.cart_add_url, {
              method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData,
            });
            const cart = await this.constructor.readCart(true);
            const Event = window.StandardEvents?.CartLinesUpdateEvent;
            if (deferred && Event) deferred.resolve({ cart: Event.createCartFromAjaxResponse(cart) });
            return cart;
          } catch (error) { deferred?.reject(error); throw error; }
        });
      }

      changeInlineQuantity(direction) {
        const selectedVariant = new FormData(this.form);
        const rules = this.quantityRules();
        return this.runInlineMutation(async () => {
          const cart = await this.constructor.readCart(true);
          const line = this.findCartLine(cart, selectedVariant);
          if (!line) return cart;
          const quantity = direction < 0 && line.quantity <= rules.min ? 0 : line.quantity + direction * rules.step;
          if (quantity > rules.max) throw new Error(`You can add up to ${rules.max} of this shade.`);
          return this.requestInlineCart(`${window.routes.cart_change_url}.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity }),
          });
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.morphWrapper) return this.addInlineQuantity();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          // Avoid re-rendering heavy collection/search pages just to refresh the bag drawer.
          const lightSectionsUrl =
            window.location.pathname.includes('/collections/') || window.location.pathname.includes('/search')
              ? '/'
              : window.location.pathname;
          formData.append('sections_url', lightSectionsUrl);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      if (this.cart) this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (this.cart) this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Your bag could not be updated. Please try again.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
